<template>
    <b-modal
        ref="modal"
        centered
        hide-footer
        no-close-on-backdrop
        size="sm"
        @hidden="onHidden"
    >
        <template #modal-title>
            <span class="font-weight-bold"><i class="bi bi-lock-fill mr-1"></i>Protected recipe</span>
        </template>

        <form class="px-1 pb-1" @submit.prevent="submit">
            <p class="text-muted mb-3">
                <strong class="text-dark">{{ recipe.title || 'This recipe' }}</strong> is protected.
                Enter its PIN to open it for {{ minutes }} minutes.
            </p>
            <div class="form-group mb-2">
                <input
                    ref="pinInput"
                    v-model="pin"
                    type="password"
                    inputmode="numeric"
                    autocomplete="off"
                    maxlength="8"
                    class="form-control text-center"
                    style="letter-spacing: .4em; font-size: 1.25rem;"
                    placeholder="PIN"
                    :disabled="submitting"
                />
            </div>
            <p v-if="error" class="text-danger small mb-2">{{ error }}</p>
            <div class="d-flex justify-content-end" style="gap: 8px;">
                <button type="button" class="btn btn-outline-secondary" :disabled="submitting" @click="$refs.modal.hide()">Cancel</button>
                <button type="submit" class="btn btn-primary" :disabled="submitting || pin.length < 4">
                    <span v-if="submitting" class="spinner-border spinner-border-sm mr-1"></span>Unlock
                </button>
            </div>
        </form>
    </b-modal>
</template>

<script>
/**
 * Asks for a protected recipe's PIN whenever the server answers 423
 * "recipe_locked" (see the interceptor in app.js), then retries every request
 * that was waiting on that recipe. The PIN is checked and the unlock held
 * server-side (RecipeLock); nothing here grants access by itself.
 */
export default {
    name: 'RecipePinPrompt',

    data() {
        return {
            recipe: {},
            pin: '',
            error: null,
            submitting: false,
            minutes: 15,
            unlocked: false,
            // Requests waiting on a recipe being unlocked: [{ recipe, retry, cancel }]
            pending: [],
        };
    },

    mounted() {
        this.$root.$on('recipe-pin-required', this.onRequired);
    },

    beforeDestroy() {
        this.$root.$off('recipe-pin-required', this.onRequired);
    },

    methods: {
        onRequired({ recipe, retry, cancel }) {
            this.pending.push({ recipe: recipe || {}, retry, cancel });
            if (!this.recipe.id) this.askNext();
        },

        askNext() {
            const next = this.pending[0];
            if (!next) return;
            this.recipe = next.recipe;
            this.pin = '';
            this.error = null;
            this.unlocked = false;
            this.$refs.modal.show();
            this.$nextTick(() => this.$refs.pinInput && this.$refs.pinInput.focus());
        },

        async submit() {
            this.error = null;
            this.submitting = true;
            try {
                const { data } = await window.axios.post(`/api/recipes/${this.recipe.id}/unlock`, { pin: this.pin });
                this.minutes = data.minutes || this.minutes;
                this.unlocked = true;
                this.$refs.modal.hide();
            } catch (e) {
                const res = e.response || {};
                this.error = (res.data && res.data.errors && res.data.errors.pin && res.data.errors.pin[0])
                    || (res.data && res.data.message) || 'Could not check the PIN. Try again.';
                this.pin = '';
            } finally {
                this.submitting = false;
            }
        },

        onHidden() {
            const id = this.recipe.id;
            const mine = this.pending.filter(p => p.recipe.id === id);
            this.pending = this.pending.filter(p => p.recipe.id !== id);
            mine.forEach(p => (this.unlocked ? p.retry() : p.cancel()));
            this.recipe = {};
            this.pin = '';
            // Another recipe was asked for meanwhile: ask for that one next.
            this.askNext();
        },
    },
};
</script>
